import SelectionState from "state/selections/SelectionState";
import { selectDefinitions } from "./selectors";
import { useDispatch, useSelector } from "react-redux";
import { useMemo } from "react";
import { useQuery } from "react-query";
import { selectBaseGroupingParams } from "state/selections/selector";
import { setDefinition } from "./actions";
import { commitSelections } from "state/selections/actions";
import scope from "./scope";
import { useIsFetching } from "react-query";

export const useColumnDefinitions = () => {
  const definition = useSelector(
    selectDefinitions(SelectionState.COMMITTED)
  )?.[0];
  const dispatch = useDispatch();
  const isFetching = useIsFetching([scope]);
  return useMemo(() => {
    const columnDefs = [];
    if (definition?.lastBuy) {
      columnDefs.push({
        headerName: "Last Buy Transaction Date",
        colId: "lastBuyDate",
        field: "lastBuyDate",
        loading: isFetching,
        X_onDelete: () => {
          dispatch(setDefinition(0, definition.setLastBuy(false), true));
          dispatch(commitSelections());
        },
        X_formatType: "DATE",
        X_dateFormat: "MM/dd/yyyy",
      });
    }
    if (definition?.lastSell) {
      columnDefs.push({
        headerName: "Last Sell Transaction Date",
        colId: "lastSellDate",
        field: "lastSellDate",
        loading: isFetching,
        X_onDelete: () => {
          dispatch(setDefinition(0, definition.setLastSell(false), true));
        },
        X_formatType: "DATE",
        X_dateFormat: "MM/dd/yyyy",
      });
    }
    return columnDefs;
  }, [definition, isFetching, dispatch]);
};

export const useRowsMap = () => {
  const dataQuery = useDataQuery();
  const { data } = dataQuery;
  return useMemo(() => {
    const [lastBuyList = [], lastSellList = []] = data ?? [];
    const map = {};
    lastBuyList.forEach((lastBuy) => {
      if (!map[lastBuy.entityId]) {
        map[lastBuy.entityId] = {};
      }
      map[lastBuy.entityId].lastBuyDate = lastBuy.lastBuyDate;
    });
    lastSellList.forEach((lastSell) => {
      if (!map[lastSell.entityId]) {
        map[lastSell.entityId] = {};
      }
      map[lastSell.entityId].lastSellDate = lastSell.lastSellDate;
    });
    return map;
  }, [data]);
};
export const useDataQuery = () => {
  const definition = useSelector(
    selectDefinitions(SelectionState.COMMITTED)
  )?.[0];
  const [baseParams] = useSelector(selectBaseGroupingParams);
  const { ids, group_type, dates } = baseParams;
  return useQuery({
    enabled: !!definition && (definition.lastBuy || definition.lastSell),
    queryKey: [
      scope,
      definition?.lastBuy,
      definition?.lastSell,
      ids,
      group_type,
      dates,
    ],
    queryFn: async () => {
      const params = {
        asOfDate: dates,
        ids,
      };

      if (group_type) {
        params.groupType = group_type;
      }
      const fetchBuy = async () => {
        if (definition.lastBuy) {
          const url = new URL(
            `${process.env.REACT_APP_BASE_URL_BUYSELL_SERVICE}/last`
          );
          url.search = new URLSearchParams({
            ...params,
            type: "buy",
          }).toString();
          const res = await fetch(url);
          return await res.json();
        }
      };
      const fetchSell = async () => {
        if (definition.lastSell) {
          const url = new URL(
            `${process.env.REACT_APP_BASE_URL_BUYSELL_SERVICE}/last`
          );
          url.search = new URLSearchParams({
            ...params,
            type: "sell",
          }).toString();
          const res = await fetch(url);
          return await res.json();
        }
      };

      return await Promise.all([fetchBuy(), fetchSell()]);
    },
  });
};
